import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import Typography, { TypographyProps } from '@mui/material/Typography';
import Box, { BoxProps } from '@mui/system/Box';
import { useMemo } from 'react';

export const FlexRow = styled(Box)<BoxProps>(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}));

type FlexColumnProps = BoxProps & { noGap?: boolean };

export const FlexColumn = styled(Box, {
  shouldForwardProp: (propName) => propName !== 'noGap',
})<FlexColumnProps>(({ noGap, theme }) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  ...(noGap ? {} : { gap: theme.spacing(0.5) }),
}));

type LabelProps = TypographyProps & { isBold?: boolean };

export const Label = styled(Typography, {
  shouldForwardProp: (propName) => propName !== 'isBold',
})<LabelProps>(({ isBold, theme }) => ({
  color: theme.brandV2.colors.treadBlack,
  fontSize: '12px',
  ...(isBold ? { fontWeight: 600 } : {}),
}));

type HintProps = TypographyProps & { isLarge?: boolean };

export const Hint = styled(Typography, {
  shouldForwardProp: (propName) => propName !== 'isLarge',
})<HintProps>(({ isLarge, theme }) => ({
  color: theme.brandV2.colors.treadGray2,
  fontSize: isLarge ? '10px' : '8px',
  letterSpacing: '0.15px',
  minHeight: isLarge ? '15px' : '12px',
}));

interface EstimateCardOwnProps {
  title: string;
  variant?: 'regular' | 'light';
}

interface EstimateCardProps extends React.PropsWithChildren<EstimateCardOwnProps> {}

export function EstimateCard({
  children,
  title,
  variant = 'regular',
}: EstimateCardProps) {
  const theme = useTheme();
  const isStringChildren = typeof children === 'string';

  const propsByVariant = useMemo(() => {
    if (variant === 'light') {
      return { container: { bgcolor: theme.semantic.surfaceOverlay } };
    }

    return {
      container: { bgcolor: theme.semantic.surfaceSunken },
    };
  }, [theme, variant]);

  return (
    <Box
      sx={{
        ...propsByVariant.container,
        border: `solid 1px ${theme.semantic.borderStrong}`,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        height: '100%',
        p: 1,
      }}
    >
      <Typography sx={{ fontSize: '10px', fontWeight: 600 }}>{title}</Typography>

      {isStringChildren ? (
        <Typography sx={{ fontSize: '18px' }}>{children}</Typography>
      ) : (
        children
      )}
    </Box>
  );
}
